import PaidIcon from '@mui/icons-material/Paid'
import SendIcon from '@mui/icons-material/Send'
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Formik, Form as FormikForm } from 'formik'
import { useCallback, useState } from 'react'

import { Shift } from '../../types/interfaces'
import { shiftCommentSchema } from '../schema/input'
import { ShiftCommentFormType } from '../types/interface'
import {
  cancelShift,
  joinShift,
  overlapShift,
  overworkedShift,
  updateShift,
} from '../utils/schedule-actions'
import SubmitButtonWrapper from './forms/SubmitButtonWrapper'
import TextfieldWrapper from './forms/TextfieldWrapper'

dayjs.extend(utc)

interface ScheduleDialogProps {
  data: {
    accepted: Shift[]
    pending: Shift[]
  }
  disableAction: boolean
  hourFormat: string
  message: (payload: {
    open: boolean
    severity: 'error' | 'info' | 'success' | 'warning'
    text: string
  }) => void
  shift: Shift
  type?: 'accepted' | 'available' | 'pending'
}

const boxColStyle = {
  sx: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    justifyContent: 'flex-start',
    mt: 1,
  },
}

const ScheduleDialog = ({
  data,
  disableAction,
  hourFormat,
  message,
  shift,
  type = 'available',
}: ScheduleDialogProps) => {
  const [open, setOpen] = useState<boolean>(false)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const START_TIME = dayjs(shift.start_time)
  const APPLIED_DATE = dayjs(shift.start_time).format('YYYY-MM-DD')
  const SHIFT_DETAILS = {
    day: dayjs(shift.start_time).format('dddd, MMMM D'),
    duration: `${shift.duration}h`,
    'end time': dayjs(shift.end_time).format(hourFormat),
    'start time': dayjs(shift.start_time).format(hourFormat),
    ...(shift.guarantee && { guarantee: `$ ${shift.guarantee}` }),
    ...(shift.guarantee_specs && {
      'guarantee details': shift.guarantee_specs,
    }),
  }
  const isOverlap = overlapShift(shift, data.pending, data.accepted)

  const isOverworked = overworkedShift(data.pending, data.accepted) // Boolean if shifts durations exceeds 12h
  const TODAY = dayjs()

  const commentInitialValues: ShiftCommentFormType = {
    comment: '',
  }

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleApply = useCallback(async () => {
    await joinShift({
      date: APPLIED_DATE,
      shift_id: shift.id || shift.parent_id,
      ...(!shift.id && {
        end_time: shift.end_time,
        exists: shift.exists,
        start_time: shift.start_time,
      }),
    })
      .then((res) => {
        handleClose()
        message({
          open: res.success,
          severity: 'success',
          text: 'Applied to shift successfully',
        })
      })
      .catch((error) => {
        message({
          open: true,
          severity: 'error',
          text: error.response.data.error,
        })
      })
  }, [APPLIED_DATE, message, shift])

  const handleCancel = useCallback(async () => {
    const CANCEL_LIMIT = dayjs(shift.start_time).diff(TODAY, 'hour')
    await cancelShift({
      shift_id: shift.id || shift.parent_id,
      ...(!shift.id && {
        end_time: shift.end_time,
        exists: shift.exists,
        start_time: shift.start_time,
      }),
      ...(type === 'accepted' &&
        CANCEL_LIMIT <= 48 && {
          late_cancel: true,
        }),
    }).then((res) => {
      handleClose()
      message({ open: res.success, severity: 'success', text: res.message })
    })
  }, [TODAY, message, shift, type])

  const handleCommentSubmit = async (
    payload: ShiftCommentFormType,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    if (!shift.application?.shift_id) {
      return
    }

    await updateShift(payload, shift.application?.shift_id).then((res) => {
      setSubmitting(false)
      message({
        open: res.success,
        severity: 'success',
        text: 'Comment sent successfully',
      })
    })
  }

  const getDialogTitle = useCallback(() => {
    if (type === 'available') {
      return `Apply for shift`
    }

    if (type === 'pending') {
      return `Shift Pending Approval`
    }

    return `Approved Shift`
  }, [type])

  const getDialogAlert = useCallback(() => {
    if (type === 'available') {
      if (isOverlap) {
        return (
          <Alert severity="warning">
            You have previously submitted an application for a shift that
            coincides with the current one. In order to proceed with the new
            shift, we kindly request that you cancel your previous overlapping
            application.
          </Alert>
        )
      }

      if (isOverworked) {
        return (
          <Alert severity="error">
            You might not be able to apply to new shifts since your work
            schedule already exceeds 12h.
          </Alert>
        )
      }

      return (
        <Alert severity="info">
          You will receive a notification once you are approved for the
          following shift.
        </Alert>
      )
    }

    if (type === 'pending') {
      return (
        <Alert severity="warning">
          You will receive a notification once your shift application is
          approved or denied.
        </Alert>
      )
    }

    return (
      <Alert severity="error">
        Cancelling might result in a penalty, affecting your reliability score.
      </Alert>
    )
  }, [isOverlap, isOverworked, type])

  const getDialogAction = useCallback(() => {
    if (type === 'available') {
      return (
        <DialogActions>
          <Button fullWidth onClick={handleClose}>
            Close
          </Button>
          {!disableAction && (
            <Button
              disabled={isOverlap}
              disableElevation
              fullWidth
              onClick={handleApply}
              variant="contained"
            >
              Apply
            </Button>
          )}
        </DialogActions>
      )
    }

    if (type === 'pending') {
      return (
        <DialogActions>
          <Button fullWidth onClick={handleClose}>
            Close
          </Button>
          {!disableAction && (
            <Button
              disableElevation
              fullWidth
              onClick={handleCancel}
              variant="contained"
            >
              Drop Application
            </Button>
          )}
        </DialogActions>
      )
    }

    return (
      <DialogActions>
        <Button fullWidth onClick={handleClose}>
          Close
        </Button>
        {!disableAction && (
          <Button
            disableElevation
            fullWidth
            onClick={handleCancel}
            variant="contained"
          >
            Can&apos;t make it
          </Button>
        )}
      </DialogActions>
    )
  }, [disableAction, handleApply, handleCancel, isOverlap, type])

  return (
    <>
      <ListItemButton
        className="MuiListItemButton-card"
        disableGutters
        onClick={handleClickOpen}
      >
        <ListItemText>
          <Box>
            <Typography variant="subtitle2">
              {`${SHIFT_DETAILS['start time']} ➜ ${SHIFT_DETAILS['end time']} (${SHIFT_DETAILS.duration})`}
            </Typography>
            <Typography
              sx={{ color: 'text.secondary', fontWeight: 800 }}
              variant="caption"
            >
              {dayjs().to(dayjs(START_TIME))}
            </Typography>
            {SHIFT_DETAILS?.guarantee && (
              <>
                <Box {...boxColStyle}>
                  <PaidIcon color="primary" fontSize="small" />
                  <Typography sx={{ fontWeight: 600 }} variant="body1">
                    {SHIFT_DETAILS?.guarantee.slice(1)}
                  </Typography>
                </Box>
                <Box {...boxColStyle}>
                  <SpeakerNotesIcon color="primary" fontSize="small" />
                  <Typography variant="body2">
                    {SHIFT_DETAILS?.['guarantee details']}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </ListItemText>
      </ListItemButton>
      <Dialog fullScreen={fullScreen} onClose={handleClose} open={open}>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {getDialogAlert()}
            <Typography variant="subtitle1">Shift Details</Typography>
            {Object.entries(SHIFT_DETAILS).map(([title, description]) => (
              <Box key={title}>
                <Typography
                  sx={{ textTransform: 'capitalize' }}
                  variant="subtitle2"
                >
                  {title}
                </Typography>
                <Typography variant="body2">{description}</Typography>
              </Box>
            ))}
            {type !== 'available' && (
              <Formik
                initialValues={commentInitialValues}
                onSubmit={async (values, { setSubmitting }) => {
                  setSubmitting(true)
                  await handleCommentSubmit(values, setSubmitting)
                }}
                validationSchema={shiftCommentSchema}
              >
                {(props) => (
                  <FormikForm>
                    <Box
                      sx={{
                        backgroundColor: grey[50],
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        px: 2,
                        py: 1,
                        rowGap: 2,
                      }}
                    >
                      <Typography variant="subtitle1">
                        Comment to scheduler
                      </Typography>
                      <TextfieldWrapper
                        fullWidth
                        helperText={`${props.values.comment.length} / 200`}
                        multiline
                        name="comment"
                        placeholder="Type in your comment..."
                        rows={3}
                        type="text"
                      />
                      <SubmitButtonWrapper
                        disabled={props.isSubmitting}
                        disableElevation
                        endIcon={<SendIcon />}
                        fullWidth
                        variant="contained"
                      >
                        Send
                      </SubmitButtonWrapper>
                    </Box>
                  </FormikForm>
                )}
              </Formik>
            )}
          </Stack>
        </DialogContent>
        {getDialogAction()}
      </Dialog>
    </>
  )
}

export default ScheduleDialog
